import React, {PureComponent} from 'react';
import withStyles from '@mui/styles/withStyles';
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import {Chip, Collapse, Dialog, DialogTitle, DialogContent, List, ListItem, ListItemText, TextField} from "@mui/material";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ExampleExerciseNewOrEditFormPopUp from "../partials/ExampleExerciseNewOrEditFormPopUp";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import {colors} from "@mui/material";
import StatCard from "../partials/StatCard";
import DashboardSection, {ChartTitle} from "../partials/DashboardSection";
import AdminLineChart from "../partials/AdminLineChart";
import AdminBarChart from "../partials/AdminBarChart";
import InfoTip from "../partials/InfoTip";

const useStyles = theme => ({});

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonthlyLabels(monthlyData) {
    return (monthlyData || []).map(m => `${MONTH_NAMES[m.month - 1]} ${m.year}`);
}

function formatMonthlyValues(monthlyData) {
    return (monthlyData || []).map(m => m.count);
}

function pct(count, total) {
    if (!total) return '0%';
    return Math.round((count / total) * 100) + '%';
}

class Admin extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            dashboard: null,
            isDashboardLoading: true,
            expandedFeedback: null,
            showAllFeedback: false,
            showExerciseManager: false,
            exampleExerciseCategories: [],
            categoryToAddExerciseFor: null,
            exerciseToEdit: null,
            latestWorkouts: [],
            daysAgo: null,
        };
    }

    componentDidMount() {
        this.fetchDashboard();
        this.fetchExerciseFormExamples();
    }

    fetchDashboard = async () => {
        this.setState({ isDashboardLoading: true });
        try {
            const response = await fetch(UrlBuilder.admin.dashboardApi());
            const json = await response.json();
            this.setState({ dashboard: json['data'], isDashboardLoading: false });
        } catch {
            this.setState({ isDashboardLoading: false });
        }
    }

    fetchExerciseFormExamples = async () => {
        await fetch(UrlBuilder.exercise.allExerciseFormExamplesApi())
            .then(response => response.json())
            .then(response => {
                this.setState({ exampleExerciseCategories: response['data'] ?? [] });
            });
    }

    fetchLatestWorkouts = async () => {
        if (this.state.daysAgo === null) return;
        await fetch(UrlBuilder.workout.workoutsApi(null, this.state.daysAgo))
            .then(response => response.json())
            .then(response => {
                this.setState({ latestWorkouts: response['data'] ?? [] });
            });
    }

    openCollapse = (categoryName) => {
        this.setState({ [categoryName]: !this.state[categoryName] });
    }

    closePopup = (shouldRefresh = false) => {
        this.setState({ categoryToAddExerciseFor: null, exerciseToEdit: null });
        if (shouldRefresh) this.fetchExerciseFormExamples();
    }

    setCategoryToAddExerciseFor = (e, cat) => {
        e.stopPropagation();
        this.setState({ categoryToAddExerciseFor: cat });
    }

    setExerciseToEdit = (exercise) => {
        this.setState({ exerciseToEdit: exercise });
    }

    handleInputChange = event => {
        this.setState({ daysAgo: event.target.value });
    }

    getDurationInHoursAndMinutes = (start, end) => {
        const durationInMs = new Date(end) - new Date(start);
        const hours = Math.floor(durationInMs / (1000 * 60 * 60));
        const minutes = Math.floor((durationInMs % (1000 * 60 * 60)) / (1000 * 60));
        return `${hours}h ${minutes}m`;
    }

    // ── Dashboard Sections ──

    renderUsersSection = (d) => {
        const u = d.users;
        return (
            <DashboardSection id="sec-users" title="Users">
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={u.totalUsers} label="Total Users" tooltip="Total registered accounts in the database (all time)" /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={u.newUsersThisWeek} label="New This Week" tooltip="Users whose account was created in the last 7 days (by DateCreatedUtc)" /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={u.newUsersThisMonth} label="New This Month" tooltip="Users whose account was created in the last 30 days (by DateCreatedUtc)" /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={u.newUsersThisYear} label="New This Year" tooltip="Users whose account was created since Jan 1 of this year (by DateCreatedUtc)" /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={u.guestSignups} label="Guest Signups" subtitle={pct(u.guestSignups, u.totalUsers)} tooltip="Users who signed up as a guest (StartedAsGuest = true). They may have later converted to a full account." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={u.regularSignups} label="Regular Signups" subtitle={pct(u.regularSignups, u.totalUsers)} tooltip="Users who signed up with email/Google/Apple (StartedAsGuest = false). Calculated as Total Users minus Guest Signups." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={u.dailyActiveUsers} label="DAU" subtitle="Today" tooltip="Distinct users who started at least one workout today (by DateTimeStartedUtc)" /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={u.weeklyActiveUsers} label="WAU" subtitle="Last 7 days" tooltip="Distinct users who started at least one workout in the last 7 days" /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={u.monthlyActiveUsers} label="MAU" subtitle="Last 30 days" tooltip="Distinct users who started at least one workout in the last 30 days" /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={u.activeLast90Days} label="Active 90d" tooltip="Distinct users who started at least one workout in the last 90 days" /></Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                    <ChartTitle tooltip="Number of new user registrations per month over the last 12 months, based on DateCreatedUtc. Users with no DateCreatedUtc are excluded.">
                        Registration Trend (Last 12 Months)
                    </ChartTitle>
                    <AdminLineChart
                        labels={formatMonthlyLabels(u.registrationTrend)}
                        data={formatMonthlyValues(u.registrationTrend)}
                        label="New Users"
                        color={colors.blue[500]}
                    />
                </Box>
            </DashboardSection>
        );
    }

    renderWorkoutsSection = (d) => {
        const w = d.workouts;
        return (
            <DashboardSection id="sec-workouts" title="Workout Activity">
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={w.totalWorkouts} label="Total Workouts" tooltip="All completed workouts all time (DateTimeEndedUtc is not null)" /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={w.workoutsToday} label="Today" tooltip="Completed workouts started today (DateTimeStartedUtc >= today UTC)" /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={w.workoutsThisWeek} label="This Week" tooltip="Completed workouts started in the last 7 days" /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={w.workoutsThisMonth} label="This Month" tooltip="Completed workouts started in the last 30 days" /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={`${w.averageDurationMinutes}m`} label="Avg Duration" tooltip="Average duration in minutes of completed workouts in the last 12 months. Calculated as (DateTimeEndedUtc - DateTimeStartedUtc) averaged across all completed workouts." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={w.averageSetsPerWorkout} label="Avg Sets/Workout" tooltip="Total exercise log entries (RoutineExerciseLogs) divided by total completed workouts. Includes all time." /></Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                    <ChartTitle tooltip="Number of completed workouts per day over the last 30 days, grouped by the date of DateTimeStartedUtc.">
                        Workouts Per Day (Last 30 Days)
                    </ChartTitle>
                    <AdminLineChart
                        labels={(w.workoutsPerDayLast30 || []).map(d => d.date.substring(5))}
                        data={(w.workoutsPerDayLast30 || []).map(d => d.count)}
                        label="Workouts"
                        color={colors.green[500]}
                    />
                </Box>
                <Box sx={{ mt: 3 }}>
                    <ChartTitle tooltip="Number of completed workouts per month over the last 12 months, grouped by month/year of DateTimeStartedUtc.">
                        Workouts Per Month (Last 12 Months)
                    </ChartTitle>
                    <AdminBarChart
                        labels={formatMonthlyLabels(w.workoutsPerMonthLast12)}
                        data={formatMonthlyValues(w.workoutsPerMonthLast12)}
                        label="Workouts"
                        color={colors.green[600]}
                    />
                </Box>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} md={6}>
                        <ChartTitle tooltip="Distribution of completed workouts by day of the week (last 12 months). Based on the UTC day of DateTimeStartedUtc.">
                            By Day of Week
                        </ChartTitle>
                        <AdminBarChart
                            labels={(w.workoutsByDayOfWeek || []).map(d => d.day.substring(0, 3))}
                            data={(w.workoutsByDayOfWeek || []).map(d => d.count)}
                            label="Workouts"
                            color={colors.teal[500]}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <ChartTitle tooltip="Distribution of completed workouts by hour of the day (0-23, UTC, last 12 months). Based on the hour component of DateTimeStartedUtc.">
                            By Hour of Day
                        </ChartTitle>
                        <AdminBarChart
                            labels={(w.workoutsByHourOfDay || []).map(d => `${d.hour}:00`)}
                            data={(w.workoutsByHourOfDay || []).map(d => d.count)}
                            label="Workouts"
                            color={colors.orange[500]}
                        />
                    </Grid>
                </Grid>
            </DashboardSection>
        );
    }

    renderEngagementSection = (d) => {
        const e = d.engagement;
        const fa = e.featureAdoption;
        const features = [
            { name: 'Goals', count: fa.usersWithGoals, tip: 'Distinct users who have created at least one exercise goal' },
            { name: 'Measurements', count: fa.usersWithMeasurements, tip: 'Distinct users who have at least one non-deleted measurement' },
            { name: 'AI Chat', count: fa.usersWithAi, tip: 'Distinct users who have started at least one AI chat session' },
            { name: 'Shared Workouts', count: fa.usersWithSharedWorkouts, tip: 'Distinct users who have shared at least one workout via link' },
            { name: 'Custom Themes', count: fa.usersWithCustomTheme, tip: 'Users who currently have a custom theme equipped (EquippedThemeId is not null)' },
            { name: 'Finish Animations', count: fa.usersWithCustomAnimation, tip: 'Users who currently have a finish animation equipped (EquippedFinishAnimationId is not null)' },
            { name: 'PR Styles', count: fa.usersWithCustomPrStyle, tip: 'Users who currently have a PR celebration style equipped (EquippedPrStyleId is not null)' },
            { name: 'Achievements', count: fa.usersWithAchievements, tip: 'Distinct users who have unlocked at least one achievement badge' },
        ];
        return (
            <DashboardSection id="sec-engagement" title="Engagement & Retention">
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={e.regularUsers} label="Regular Users" subtitle="3+ workouts/30d" tooltip="Users who completed 3 or more workouts in the last 30 days. Grouped by UserId on completed workouts." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={e.powerUsers} label="Power Users" subtitle="10+ workouts/30d" tooltip="Users who completed 10 or more workouts in the last 30 days. Subset of regular users." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={e.usersWithActiveStreaks} label="Active Streaks" tooltip="Users whose CurrentWeekStreak is greater than 0. A streak increments when a user meets their weekly workout goal (default 3/week) for consecutive weeks." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={e.averageStreakLength} label="Avg Streak" subtitle="weeks" tooltip="Average CurrentWeekStreak among users who have a streak > 0. In weeks." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={e.maxStreakLength} label="Longest Streak" subtitle="weeks" tooltip="The highest LongestWeekStreak value across all users (all time record)." /></Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                    <ChartTitle tooltip="How many distinct users have used each feature. Percentage is relative to total registered users.">
                        Feature Adoption
                    </ChartTitle>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Feature</TableCell>
                                    <TableCell align="right">Users</TableCell>
                                    <TableCell align="right">% of Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {features.map(f => (
                                    <TableRow key={f.name}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {f.name}
                                                <InfoTip text={f.tip} />
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">{f.count}</TableCell>
                                        <TableCell align="right">{pct(f.count, fa.totalUsers)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </DashboardSection>
        );
    }

    renderRevenueSection = (d) => {
        const r = d.revenue;
        return (
            <DashboardSection id="sec-revenue" title="Revenue & Subscriptions">
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={r.activeSubscribers} label="Active Subscribers" tooltip="Subscriptions where EndDate is null or in the future. Currently only the AI Access Plan subscription exists." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={r.totalPurchases} label="Total Purchases" tooltip="Total rows in UserPurchases table (all time). Includes subscriptions, donations, and customization items acknowledged via Google Play." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={r.totalDonations} label="Donations" tooltip="Total rows in the Donations table (all time)." /></Grid>
                </Grid>
                {r.purchasesByProduct && r.purchasesByProduct.length > 0 &&
                    <Box sx={{ mt: 3 }}>
                        <ChartTitle tooltip="Count of UserPurchase records grouped by ProductIdentifier (the Google Play product SKU). Sorted by count descending.">
                            Purchases by Product
                        </ChartTitle>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Product</TableCell>
                                        <TableCell align="right">Count</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {r.purchasesByProduct.map(p => (
                                        <TableRow key={p.productIdentifier}>
                                            <TableCell>{p.productIdentifier}</TableCell>
                                            <TableCell align="right">{p.count}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                }
                {r.purchaseTrendMonthly && r.purchaseTrendMonthly.length > 0 &&
                    <Box sx={{ mt: 3 }}>
                        <ChartTitle tooltip="Number of UserPurchase records per month over the last 12 months, based on CreatedAt timestamp.">
                            Purchase Trend (Last 12 Months)
                        </ChartTitle>
                        <AdminBarChart
                            labels={formatMonthlyLabels(r.purchaseTrendMonthly)}
                            data={formatMonthlyValues(r.purchaseTrendMonthly)}
                            label="Purchases"
                            color={colors.amber[600]}
                        />
                    </Box>
                }
                {r.recentDonations && r.recentDonations.length > 0 &&
                    <Box sx={{ mt: 3 }}>
                        <ChartTitle tooltip="Last 20 donations ordered by DateCreated descending.">
                            Recent Donations
                        </ChartTitle>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell align="right">Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {r.recentDonations.map((don, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{don.displayName || 'Anonymous'}</TableCell>
                                            <TableCell align="right">{new Date(don.dateCreated).toLocaleDateString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                }
            </DashboardSection>
        );
    }

    renderAiSection = (d) => {
        const ai = d.ai;
        return (
            <DashboardSection id="sec-ai" title="AI Usage">
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={ai.totalMessages} label="Total Messages" tooltip="Total rows in the ChatMessages table (all time). Includes both user and assistant messages." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={ai.messagesThisMonth} label="This Month" tooltip="ChatMessages with CreatedAtUtc in the last 30 days." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={ai.uniqueAiUsers} label="Unique AI Users" tooltip="Distinct UserIds from the Chats table (users who have started at least one AI chat session, all time)." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={ai.averageMessagesPerUser} label="Avg Msgs/User" tooltip="Total ChatMessages divided by Unique AI Users. Rounded to 1 decimal." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={ai.thumbsUp} label="Thumbs Up" tooltip="ChatMessages where the user gave positive feedback (Feedback = 'up')." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={ai.thumbsDown} label="Thumbs Down" tooltip="ChatMessages where the user gave negative feedback (Feedback = 'down')." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={ai.totalCreditsUsed} label="Credits Used" tooltip="Sum of CreditsUsed across all rows in UserFeatureUsages. Tracks AI feature consumption across all users and all periods." /></Grid>
                </Grid>
                {ai.messagesPerMonthLast12 && ai.messagesPerMonthLast12.length > 0 &&
                    <Box sx={{ mt: 3 }}>
                        <ChartTitle tooltip="Number of ChatMessage records per month over the last 12 months, based on CreatedAtUtc.">
                            AI Messages Per Month (Last 12 Months)
                        </ChartTitle>
                        <AdminLineChart
                            labels={formatMonthlyLabels(ai.messagesPerMonthLast12)}
                            data={formatMonthlyValues(ai.messagesPerMonthLast12)}
                            label="Messages"
                            color={colors.purple[500]}
                        />
                    </Box>
                }
            </DashboardSection>
        );
    }

    renderTopEngagedByWorkoutsSection = (d) => {
        const users = d.topEngagedByWorkouts || [];
        if (users.length === 0) return null;
        return (
            <DashboardSection id="sec-top-workouts" title="Top 15 Most Engaged (Workouts, 90d)">
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell align="right">Workouts</TableCell>
                                <TableCell align="right">Registered</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((u, i) => (
                                <TableRow key={i}>
                                    <TableCell>{i + 1}</TableCell>
                                    <TableCell>{u.name || '-'}</TableCell>
                                    <TableCell>{u.email || '-'}</TableCell>
                                    <TableCell align="right">{u.count}</TableCell>
                                    <TableCell align="right">{u.dateRegistered ? new Date(u.dateRegistered).toLocaleDateString() : '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DashboardSection>
        );
    }

    renderTopEngagedByPurchasesSection = (d) => {
        const users = d.topEngagedByPurchases || [];
        if (users.length === 0) return null;
        return (
            <DashboardSection id="sec-top-purchases" title="Top 15 Most Engaged (Purchases + Donations)">
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell align="right">Purchases</TableCell>
                                <TableCell align="right">Registered</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((u, i) => (
                                <TableRow key={i}>
                                    <TableCell>{i + 1}</TableCell>
                                    <TableCell>{u.name || '-'}</TableCell>
                                    <TableCell>{u.email || '-'}</TableCell>
                                    <TableCell align="right">{u.count}</TableCell>
                                    <TableCell align="right">{u.dateRegistered ? new Date(u.dateRegistered).toLocaleDateString() : '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DashboardSection>
        );
    }

    renderLatestRegisteredUsersSection = (d) => {
        const users = d.latestRegisteredUsers || [];
        if (users.length === 0) return null;
        return (
            <DashboardSection id="sec-latest-users" title="Latest Registered Users">
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Registered</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((u, i) => (
                                <TableRow key={i}>
                                    <TableCell>{i + 1}</TableCell>
                                    <TableCell>{u.name || '-'}</TableCell>
                                    <TableCell>{u.email || '-'}</TableCell>
                                    <TableCell>{u.isGuest ? 'Guest' : 'Regular'}</TableCell>
                                    <TableCell align="right">{u.dateRegistered ? new Date(u.dateRegistered).toLocaleDateString() : '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DashboardSection>
        );
    }

    renderContentSection = (d) => {
        const c = d.content;
        return (
            <DashboardSection id="sec-content" title="Content & Growth">
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={c.totalExercisesCreated} label="Exercises Created" tooltip="Total user-created exercises in the Exercises table (all users, all time). Does not include the pre-seeded example exercises." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={c.totalRoutines} label="Total Routines" tooltip="Total rows in the Routines table (all users, all time). Includes archived routines." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={c.activeRoutines} label="Active Routines" tooltip="Routines where IsArchived = false." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={c.totalGoals} label="Total Goals" tooltip="Total rows in the ExerciseGoals table (all users, all time)." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={c.completedGoals} label="Completed Goals" tooltip="ExerciseGoals where DateCompleted is not null." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={c.activeGoals} label="Active Goals" tooltip="ExerciseGoals where DateCompleted is null. Calculated as Total Goals minus Completed Goals." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={c.sharedWorkoutsCount} label="Shared Workouts" tooltip="Total rows in the SharedWorkouts table. Each row represents a unique shareable link generated for a workout." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={c.feedbackSubmissions} label="Feedback" tooltip="Total rows in the Feedbacks table (all users, all time)." /></Grid>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={c.totalAchievementsUnlocked} label="Achievements Unlocked" tooltip="Total rows in the Achievements table. Each row is one badge unlocked by one user. A single user can have up to 71 badges." /></Grid>
                </Grid>
            </DashboardSection>
        );
    }

    renderFeedbackRow = (f) => {
        return (
            <TableRow key={f.id}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(f.dateCreated).toLocaleDateString()}
                </TableCell>
                <TableCell>{f.userName || '-'}</TableCell>
                <TableCell>{f.email || '-'}</TableCell>
                <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                        cursor: 'pointer',
                    }}
                        onClick={() => this.setState({ expandedFeedback: f })}
                    >
                        {f.text}
                    </Typography>
                </TableCell>
                <TableCell align="right">
                    {f.email &&
                        <Button
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                            href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(f.email)}&su=${encodeURIComponent('Re: Your WIP Feedback')}&body=${encodeURIComponent(`Hi ${f.userName || ''},\n\nThanks for your feedback:\n"${f.text}"\n\n`)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Reply
                        </Button>
                    }
                </TableCell>
            </TableRow>
        );
    }

    renderFeedbackSection = (d) => {
        const fb = d.feedback;
        const items = fb.recentFeedback || [];
        const top5 = items.slice(0, 5);
        const rest = items.slice(5);
        return (
            <DashboardSection id="sec-feedback" title="User Feedback">
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={4} md={3}><StatCard value={fb.totalCount} label="Total Feedback" tooltip="Total rows in the Feedbacks table (all users, all time)." /></Grid>
                </Grid>
                {items.length > 0 &&
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>User</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Feedback</TableCell>
                                    <TableCell align="right">Reply</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {top5.map(f => this.renderFeedbackRow(f))}
                                {rest.length > 0 && this.state.showAllFeedback &&
                                    rest.map(f => this.renderFeedbackRow(f))
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                }
                {rest.length > 0 &&
                    <Button
                        size="small"
                        onClick={() => this.setState({ showAllFeedback: !this.state.showAllFeedback })}
                        sx={{ mt: 1, textTransform: 'none' }}
                    >
                        {this.state.showAllFeedback ? 'Show less' : `Show ${rest.length} more`}
                    </Button>
                }
                <Dialog
                    open={Boolean(this.state.expandedFeedback)}
                    onClose={() => this.setState({ expandedFeedback: null })}
                    fullWidth
                    maxWidth="sm"
                >
                    {this.state.expandedFeedback && <>
                        <DialogTitle sx={{ pb: 0 }}>
                            Feedback from {this.state.expandedFeedback.userName || 'Unknown'}
                            <Typography variant="caption" display="block" color="text.secondary">
                                {new Date(this.state.expandedFeedback.dateCreated).toLocaleDateString()}
                                {this.state.expandedFeedback.email && ` — ${this.state.expandedFeedback.email}`}
                            </Typography>
                        </DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                                {this.state.expandedFeedback.text}
                            </Typography>
                            {this.state.expandedFeedback.email &&
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{ mt: 2, textTransform: 'none' }}
                                    href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(this.state.expandedFeedback.email)}&su=${encodeURIComponent('Re: Your WIP Feedback')}&body=${encodeURIComponent(`Hi ${this.state.expandedFeedback.userName || ''},\n\nThanks for your feedback:\n"${this.state.expandedFeedback.text}"\n\n`)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Reply via Gmail
                                </Button>
                            }
                        </DialogContent>
                    </>}
                </Dialog>
            </DashboardSection>
        );
    }

    // ── Exercise Manager (existing functionality) ──

    renderExerciseManager = () => {
        return (
            <Box sx={{ mt: 2 }}>
                <ExampleExerciseNewOrEditFormPopUp
                    exercise={this.state.exerciseToEdit}
                    category={this.state.categoryToAddExerciseFor}
                    closeSelfFunc={(shouldRefresh) => this.closePopup(shouldRefresh)}
                />

                <TextField
                    label="Days Ago"
                    type="number"
                    value={this.state.daysAgo}
                    onChange={this.handleInputChange}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                />
                <Button type="submit" variant="contained" color="primary" onClick={this.fetchLatestWorkouts}>
                    Submit
                </Button>

                {this.state.latestWorkouts.length > 0 &&
                    <Box sx={{ mt: 2 }}>
                        <List>
                            {this.state.latestWorkouts.slice().reverse().map((workout, index) => (
                                <React.Fragment key={workout.id}>
                                    <ListItem>
                                        <ListItemText
                                            primary={
                                                <>
                                                    <Typography variant="subtitle1">{`${this.state.latestWorkouts.length - index}. ${workout.user.name}`}</Typography>
                                                    <Typography variant="body2" color="textSecondary">{`Email: ${workout.user.email}`}</Typography>
                                                </>
                                            }
                                            secondary={
                                                <>
                                                    <Typography variant="body2" color="textSecondary">{`Started: ${new Date(workout.dateTimeStartedUtc).toLocaleString()}`}</Typography>
                                                    {workout.dateTimeEndedUtc && (
                                                        <Typography variant="body2" color="textSecondary">{`Ended: ${new Date(workout.dateTimeEndedUtc).toLocaleString()}`}</Typography>
                                                    )}
                                                    {workout.dateTimeEndedUtc && (
                                                        <Typography variant="body2" color="textSecondary">{`Duration: ${this.getDurationInHoursAndMinutes(workout.dateTimeStartedUtc, workout.dateTimeEndedUtc)}`}</Typography>
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                    {index !== this.state.latestWorkouts.length - 1 && <Divider variant="inset" component="li" />}
                                </React.Fragment>
                            ))}
                        </List>
                    </Box>
                }

                <Typography variant='h4' sx={{ mt: 3 }}>Categories</Typography>
                <List>
                    {this.state.exampleExerciseCategories.map(category =>
                        <React.Fragment key={category.name}>
                            <ListItem button divider onClick={() => this.openCollapse(category.name)}>
                                <ListItemText>{category.name}</ListItemText>
                                <Button onClick={(e) => this.setCategoryToAddExerciseFor(e, category)}>ADD NEW</Button>
                            </ListItem>
                            <Collapse in={!!this.state[category.name]}>
                                <List dense>
                                    {category.exampleExercises.map(exercise =>
                                        <ListItem key={exercise.id} divider button onClick={() => this.setExerciseToEdit(exercise)}>
                                            {exercise.name}
                                        </ListItem>
                                    )}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    )}
                </List>
            </Box>
        );
    }

    render() {
        const { dashboard, isDashboardLoading } = this.state;

        return (
            <Box sx={{ p: 2, maxWidth: 1200 }}>
                <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>Admin Dashboard</Typography>

                {dashboard &&
                    <Box sx={{
                        position: 'sticky', top: 64, zIndex: 10,
                        py: 1, mb: 2,
                        backgroundColor: 'background.paper',
                        display: 'flex', flexWrap: 'wrap', gap: 0.5,
                        borderBottom: 1, borderColor: 'divider',
                    }}>
                        {[
                            { id: 'sec-feedback', label: 'Feedback' },
                            { id: 'sec-users', label: 'Users' },
                            { id: 'sec-top-workouts', label: 'Top Workouts' },
                            { id: 'sec-top-purchases', label: 'Top Purchases' },
                            { id: 'sec-latest-users', label: 'Latest Users' },
                            { id: 'sec-workouts', label: 'Workouts' },
                            { id: 'sec-engagement', label: 'Engagement' },
                            { id: 'sec-revenue', label: 'Revenue' },
                            { id: 'sec-ai', label: 'AI' },
                            { id: 'sec-content', label: 'Content' },
                        ].map(s => (
                            <Chip
                                key={s.id}
                                label={s.label}
                                size="small"
                                clickable
                                onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' })}
                            />
                        ))}
                    </Box>
                }

                {isDashboardLoading &&
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress />
                    </Box>
                }

                {dashboard && <>
                    {this.renderFeedbackSection(dashboard)}
                    {this.renderUsersSection(dashboard)}
                    {this.renderTopEngagedByWorkoutsSection(dashboard)}
                    {this.renderTopEngagedByPurchasesSection(dashboard)}
                    {this.renderLatestRegisteredUsersSection(dashboard)}
                    {this.renderWorkoutsSection(dashboard)}
                    {this.renderEngagementSection(dashboard)}
                    {this.renderRevenueSection(dashboard)}
                    {this.renderAiSection(dashboard)}
                    {this.renderContentSection(dashboard)}
                </>}

                <Divider sx={{ my: 4 }} />

                <Box>
                    <Button
                        variant="outlined"
                        onClick={() => this.setState({ showExerciseManager: !this.state.showExerciseManager })}
                        sx={{ textTransform: 'none' }}
                    >
                        {this.state.showExerciseManager ? 'Hide' : 'Show'} Exercise Manager
                    </Button>

                    {this.state.showExerciseManager && this.renderExerciseManager()}
                </Box>
            </Box>
        );
    }
}

export default withStyles(useStyles)(Admin);
